import { useMemo } from 'react';
import { Infer, object, optional, string } from 'superstruct';

import { useFeatureServices } from './use-feature-services';

// Updated structure to match the simplified schema
const Content = object({
  mobileImage: optional(string()),
  tabletImage: optional(string()),
  desktopImage: optional(string()),
});

export type FeatureAppContent = Infer<typeof Content>;

// Default background images - defined outside component to prevent recreation
const DEFAULT_BACKGROUND_IMAGES = {
  mobileImage:
    '/content/dam/onehub_pkw/importers/us/en/owners/welcome-screen-mobile.png',
  tabletImage:
    '/content/dam/onehub_pkw/importers/us/en/owners/welcome-screen-tablet.png',
  desktopImage:
    '/content/dam/onehub_pkw/importers/us/en/owners/welcome-screen-desktop.png',
};

// Helper function to validate if all required image properties are present and valid
const isValidImageSet = (images: any): boolean => {
  return (
    images &&
    typeof images === 'object' &&
    typeof images.mobileImage === 'string' &&
    images.mobileImage.trim() !== '' &&
    typeof images.tabletImage === 'string' &&
    images.tabletImage.trim() !== '' &&
    typeof images.desktopImage === 'string' &&
    images.desktopImage.trim() !== ''
  );
};

export function useFeatureAppContent(): {
  backgroundImages: typeof DEFAULT_BACKGROUND_IMAGES;
} {
  const { 'fa-content-service': contentService } = useFeatureServices();
  const content = contentService?.content;

  // Memoize the result to prevent unnecessary re-renders
  return useMemo(() => {
    if (!content || typeof content !== 'object') {
      return {
        backgroundImages: DEFAULT_BACKGROUND_IMAGES,
      };
    }

    // Check if content has backgroundImages structure
    if (
      'backgroundImages' in content &&
      isValidImageSet(content.backgroundImages)
    ) {
      const { backgroundImages } = content as any;
      return {
        backgroundImages: {
          mobileImage: backgroundImages.mobileImage,
          tabletImage: backgroundImages.tabletImage,
          desktopImage: backgroundImages.desktopImage,
        },
      };
    }

    // Check if content has the image properties directly
    if (isValidImageSet(content)) {
      const { mobileImage, tabletImage, desktopImage } = content as any;
      return {
        backgroundImages: {
          mobileImage,
          tabletImage,
          desktopImage,
        },
      };
    }

    // Return default values if content is not available or invalid
    return {
      backgroundImages: DEFAULT_BACKGROUND_IMAGES,
    };
  }, [content]); // Only re-compute when content actually changes
}
